import type {
  GraphSeries,
  DataPoint,
  GroupColumn,
} from "src/components/charts/engine/types";
import { chartColors } from "src/pages/analytics/constants";
import type { BarChartData } from "@hightouch/lib/llm/schema/components/schemas";
import type { CHART_BAR_COLORS } from "@hightouch/lib/llm/schema/components/schemas";
import { AggregationOption } from "@hightouch/lib/query/visual/types";
import { CHART_COLORS } from "src/ui/colors";

const NAMED_BAR_COLORS: Record<(typeof CHART_BAR_COLORS)[number], string> = {
  blue: CHART_COLORS.blue.base,
  yellow: CHART_COLORS.yellow.base,
  purple: CHART_COLORS.purple.base,
  green: CHART_COLORS.green.base,
  orange: CHART_COLORS.orange.base,
  brown: CHART_COLORS.brown.base,
  pink: CHART_COLORS.pink.base,
  red: CHART_COLORS.red.base,
  gray: CHART_COLORS.gray.base,
};

// Required but unused for LLM charts
const UNUSED_VALUES = {
  metricId: "",
  rawMetricName: "",
  audienceName: "",
  metricNoun: "row" as const,
  aggregation: AggregationOption.Count,
};

/**
 * Create a grouping column for LLM charts
 * Uses a stub RawColumn structure since we don't have real model references
 */
function createGroupColumn(name: string, value: string): GroupColumn {
  return {
    column: {
      type: "raw" as const,
      name,
      modelId: "", // Stub: LLM charts don't have real model references
    },
    value,
    alias: name,
  };
}

function createDataPoint({
  calculatedAt,
  seriesKey,
  value,
  tooltip,
  color,
}: {
  calculatedAt: number;
  seriesKey: string;
  value: number;
  tooltip?: string;
  color?: string;
}): DataPoint {
  return {
    calculatedAt,
    seriesKey,
    metricValue: value,
    metricName: seriesKey,
    description: tooltip ?? undefined,
    color,

    // Required but unused for LLM charts
    aggregation: AggregationOption.Count,
    audienceId: "",
    splitId: null,
  };
}

/**
 * Transform bar chart data to GraphSeries format for vertical column charts
 * Creates one series per metric with multiple data points
 */
export function transformChartToGraphSeries(
  chartData: BarChartData,
): GraphSeries[] {
  return chartData.series.map((series, index) => ({
    key: series.name.toLowerCase().replace(/\s+/g, "-"),
    description: series.name,
    legendName: series.name,
    metricKey: series.name,
    metricName: series.name,

    // Styling - assign color based on series index
    color: chartColors[index % chartColors.length]!.color,
    style: "solid" as const,

    ...UNUSED_VALUES,

    // Data transformation
    data: series.data.map((point, idx) =>
      createDataPoint({
        // For categorical data, use index as calculatedAt for x-axis positioning.
        // The categoricalLabels prop passed to AnalyticsGraph will be used to
        // display the correct labels on the x-axis and in tooltips.
        calculatedAt: idx,
        seriesKey: series.name,
        value: point.value,
        tooltip: point.tooltip,
        // The agent may pin a named color per bar (same lookup as the
        // horizontal transform); unset points keep the series color.
        color: point.color ? NAMED_BAR_COLORS[point.color] : undefined,
      }),
    ),
  }));
}

/**
 * Transform bar chart data to GraphSeries format for horizontal bar charts (breakdown table)
 * Flattens into one series per category, with series name as grouping if multiple series
 */
export function transformChartToHorizontalBarGraphSeries(
  chartData: BarChartData,
): GraphSeries[] {
  const hasMultipleSeries = chartData.series.length > 1;

  // Flatten all series into individual rows for the breakdown table
  return chartData.series.flatMap((series, seriesIndex) =>
    series.data.map((point, pointIndex) => ({
      key: `${series.name}-${point.label}`.toLowerCase().replace(/\s+/g, "-"),
      description: point.label,
      legendName: point.label,
      metricKey: `${series.name}-${point.label}`,
      metricName: point.label,

      // Styling - the agent may pin a named color per bar (e.g. to visually
      // group related bars); otherwise cycle the palette by global index.
      color: point.color
        ? NAMED_BAR_COLORS[point.color]
        : chartColors[
            (seriesIndex * series.data.length + pointIndex) % chartColors.length
          ]!.color,
      style: "solid" as const,

      ...UNUSED_VALUES,

      // If multiple series, add series name as a grouping column
      grouping: hasMultipleSeries
        ? [createGroupColumn("Series", series.name)]
        : undefined,

      // Single data point per row for breakdown table
      data: [
        createDataPoint({
          calculatedAt: 0,
          seriesKey: point.label,
          value: point.value,
          tooltip: point.tooltip,
        }),
      ],
    })),
  );
}
